/**
 * Diálogo admin: gestiona pipelines, sus etapas y sus campos personalizados.
 *
 * Tabs:
 *  - General: datos del pipeline (nombre, color, moneda)
 *  - Etapas: lista editable + reordenar + crear/borrar
 *  - Campos: lista de campos personalizados
 *
 * Y abajo: crear pipeline NUEVO desde cero.
 */
import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Plus, Trash2, Save, Loader2, Search, ChevronUp, ChevronDown, Copy as CopyIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../App";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_TYPES = [
  { value: "text", label: "Texto corto" },
  { value: "textarea", label: "Texto largo" },
  { value: "number", label: "Número" },
  { value: "currency", label: "Moneda" },
  { value: "date", label: "Fecha" },
  { value: "datetime", label: "Fecha y hora" },
  { value: "time", label: "Hora" },
  { value: "select", label: "Desplegable" },
  { value: "multiselect", label: "Desplegable múltiple" },
  { value: "checkbox", label: "Sí / No" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Teléfono" },
];

export default function PipelineAdminDialog({ pipelines, activePipeline, onClose, onChanged }) {
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador", "superadmin"].includes((user?.role || "").toLowerCase());
  const [pipelineId, setPipelineId] = useState(activePipeline.id);
  const [p, setP] = useState(activePipeline);
  const [saving, setSaving] = useState(false);
  const [optionsDraft, setOptionsDraft] = useState({});
  const [depValuesDraft, setDepValuesDraft] = useState({});
  const [newReqDraft, setNewReqDraft] = useState({}); // {stageId: {label, type}}

  // Crea un campo nuevo desde el bloque de obligatorios de una etapa y lo marca obligatorio ahí mismo
  const createFieldAndRequire = async (stage) => {
    const draft = newReqDraft[stage.id] || {};
    const label = (draft.label || "").trim();
    if (!label) { toast.error("Escribí la etiqueta del campo nuevo"); return; }
    const name = label.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    if ((p.fields || []).some((x) => x.name === name)) {
      toast.error(`Ya existe un campo con el nombre interno "${name}". Marcálo en la lista.`);
      return;
    }
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}/fields`, {
        method: "POST", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ name, label, field_type: draft.type || "text", options: [], required: false, order: (p.fields || []).length }),
      });
      if (!r.ok) throw new Error();
      const cur = stage.required_fields || [];
      const r2 = await fetch(`${API_URL}/api/pipelines/stages/${stage.id}`, {
        method: "PUT", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ ...stage, required_fields: [...cur, name] }),
      });
      if (!r2.ok) throw new Error();
      toast.success(`Campo "${label}" creado y marcado obligatorio en "${stage.name}"`);
      setNewReqDraft((prev) => { const n = { ...prev }; delete n[stage.id]; return n; });
      reloadPipeline();
    } catch { toast.error("Error al crear el campo"); }
  };

  const reloadPipeline = async (pid = pipelineId) => {
    const r = await fetch(`${API_URL}/api/pipelines/${pid}`, getAuthHeaders());
    const d = await r.json();
    setP(d);
    onChanged?.();
  };

  const handlePickPipeline = async (newId) => {
    setPipelineId(newId);
    const r = await fetch(`${API_URL}/api/pipelines/${newId}`, getAuthHeaders());
    setP(await r.json());
  };

  // --- General ---
  const handleSaveGeneral = async () => {
    setSaving(true);
    try {
      const payload = {
        name: p.name, description: p.description || "", color: p.color || "#193966",
        icon: p.icon || "Briefcase", currency: p.currency || "DOP",
        show_totals: p.show_totals !== false,
        hide_value_fields: p.hide_value_fields === true,
        titulo_cotejar_clientes: p.titulo_cotejar_clientes !== false, activo: true,
      };
      const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}`, {
        method: "PUT", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error();
      toast.success("Pipeline actualizado");
      onChanged?.();
    } catch { toast.error("Error al guardar"); }
    setSaving(false);
  };

  const handleCreatePipeline = async () => {
    const name = window.prompt("Nombre del nuevo pipeline:");
    if (!name?.trim()) return;
    try {
      const r = await fetch(`${API_URL}/api/pipelines`, {
        method: "POST", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), description: "", color: "#193966", icon: "Briefcase", currency: "DOP", activo: true }),
      });
      const created = await r.json();
      toast.success("Pipeline creado. Agregale etapas y campos.");
      handlePickPipeline(created.id);
      onChanged?.();
    } catch { toast.error("Error al crear pipeline"); }
  };

  const handleClonePipeline = async () => {
    if (!window.confirm(`¿Duplicar el pipeline "${p.name}" con sus etapas y campos? (No copia los negocios ni los permisos)`)) return;
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}/clone`, {
        method: "POST", headers: getAuthHeaders().headers,
      });
      if (!r.ok) { const e = await r.json(); throw new Error(typeof e.detail === "string" ? e.detail : "Error"); }
      const d = await r.json();
      toast.success(`Pipeline duplicado como "${d.name}" (${d.stages} etapas, ${d.fields} campos)`);
      onChanged?.();
      handlePickPipeline(d.id);
    } catch (err) { toast.error(err.message || "Error al duplicar el pipeline"); }
  };

  const handleDeletePipeline = async () => {
    if (!window.confirm(`Eliminar el pipeline "${p.name}"? (Soft delete, las tarjetas quedan archivadas)`)) return;
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}`, { method: "DELETE", headers: getAuthHeaders().headers });
      if (!r.ok) throw new Error();
      toast.success("Pipeline eliminado");
      onClose();
      onChanged?.();
    } catch { toast.error("Error al eliminar"); }
  };

  // --- Stages ---
  const addStage = async () => {
    const name = window.prompt("Nombre de la etapa:");
    if (!name?.trim()) return;
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}/stages`, {
        method: "POST", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), order: (p.stages || []).length, color: "#64748B", is_won: false, is_lost: false, default_probability: 20 }),
      });
      if (!r.ok) throw new Error();
      reloadPipeline();
    } catch { toast.error("Error al crear etapa"); }
  };
  const updateStage = async (stage, patch) => {
    try {
      const r = await fetch(`${API_URL}/api/pipelines/stages/${stage.id}`, {
        method: "PUT", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ ...stage, ...patch }),
      });
      if (!r.ok) throw new Error();
      reloadPipeline();
    } catch { toast.error("Error al actualizar etapa"); }
  };
  const moveStage = async (stage, dir) => {
    const arr = [...(p.stages || [])];
    const i = arr.findIndex((x) => x.id === stage.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setP({ ...p, stages: arr });
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}/stages/reorder`, {
        method: "POST", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ ids: arr.map((x) => x.id) }),
      });
      if (!r.ok) throw new Error();
      reloadPipeline();
    } catch { toast.error("Error al reordenar etapas"); }
  };
  const deleteStage = async (stage) => {
    if (!window.confirm(`Eliminar la etapa "${stage.name}"?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/pipelines/stages/${stage.id}`, { method: "DELETE", headers: getAuthHeaders().headers });
      if (!r.ok) {
        const e = await r.json(); throw new Error(e.detail || "Error");
      }
      reloadPipeline();
    } catch (err) { toast.error(err.message || "Error al eliminar"); }
  };

  // --- Fields ---
  const addField = async () => {
    const label = window.prompt("Etiqueta del campo (ej. 'Cliente'):");
    if (!label?.trim()) return;
    const name = label.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}/fields`, {
        method: "POST", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ name, label: label.trim(), field_type: "text", options: [], required: false, order: (p.fields || []).length }),
      });
      if (!r.ok) throw new Error();
      reloadPipeline();
    } catch { toast.error("Error al crear campo"); }
  };
  const updateField = async (field, patch) => {
    try {
      const r = await fetch(`${API_URL}/api/pipelines/fields/${field.id}`, {
        method: "PUT", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ ...field, ...patch }),
      });
      if (!r.ok) throw new Error();
      reloadPipeline();
    } catch { toast.error("Error al actualizar campo"); }
  };
  const deleteField = async (field) => {
    if (!window.confirm(`Eliminar el campo "${field.label}"?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/pipelines/fields/${field.id}`, { method: "DELETE", headers: getAuthHeaders().headers });
      if (!r.ok) throw new Error();
      reloadPipeline();
    } catch { toast.error("Error al eliminar campo"); }
  };
  const moveField = async (field, dir) => {
    const arr = [...(p.fields || [])];
    const i = arr.findIndex((x) => x.id === field.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setP({ ...p, fields: arr });
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}/fields/reorder`, {
        method: "POST", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ ids: arr.map((x) => x.id) }),
      });
      if (!r.ok) throw new Error();
      reloadPipeline();
    } catch { toast.error("Error al reordenar campos"); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="pipeline-admin-dialog">
        <DialogHeader>
          <DialogTitle>Administrar pipelines</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <select value={pipelineId} onChange={(e) => handlePickPipeline(e.target.value)} className="h-9 border border-slate-300 rounded px-2 text-sm bg-white flex-1 min-w-[200px]">
            {pipelines.map((pp) => <option key={pp.id} value={pp.id}>{pp.name}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={handleCreatePipeline} data-testid="admin-new-pipeline-btn">
            <Plus className="w-3 h-3 mr-1" /> Nuevo pipeline
          </Button>
          <Button size="sm" variant="outline" onClick={handleClonePipeline} className="text-[#193966] border-[#193966]/30" data-testid="admin-clone-pipeline-btn">
            <CopyIcon className="w-3 h-3 mr-1" /> Duplicar
          </Button>
          <Button size="sm" variant="outline" onClick={handleDeletePipeline} className="text-rose-600 border-rose-200">
            <Trash2 className="w-3 h-3 mr-1" /> Eliminar
          </Button>
        </div>
        <Tabs defaultValue="general">
          <TabsList>
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="etapas">Etapas ({p.stages?.length || 0})</TabsTrigger>
            <TabsTrigger value="campos">Campos ({p.fields?.length || 0})</TabsTrigger>
            <TabsTrigger value="permisos" data-testid="admin-tab-permisos">Permisos</TabsTrigger>
            <TabsTrigger value="tareas" data-testid="admin-tab-tareas">Tareas</TabsTrigger>
          </TabsList>

          {/* GENERAL */}
          <TabsContent value="general" className="space-y-3 pt-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nombre</Label>
                <Input value={p.name || ""} onChange={(e) => setP({ ...p, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Moneda</Label>
                <Input value={p.currency || ""} onChange={(e) => setP({ ...p, currency: e.target.value })} maxLength={5} />
              </div>
              <div>
                <Label className="text-xs">Color</Label>
                <Input type="color" value={p.color || "#193966"} onChange={(e) => setP({ ...p, color: e.target.value })} className="h-9" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Descripción</Label>
              <Textarea value={p.description || ""} onChange={(e) => setP({ ...p, description: e.target.value })} rows={2} />
            </div>
            <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
              <input
                type="checkbox"
                id="pipeline-show-totals"
                checked={p.show_totals !== false}
                onChange={(e) => setP({ ...p, show_totals: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-[#193966]"
                data-testid="admin-show-totals"
              />
              <label htmlFor="pipeline-show-totals" className="text-xs text-slate-700 cursor-pointer">
                <span className="font-medium">Totalizar montos ({p.currency || "DOP"}) al final de cada etapa</span>
                <span className="block text-[11px] text-slate-500">
                  Desactivá esta opción en pipelines que no manejan dinero (ej. Visitas) para ocultar los totales al pie de cada columna.
                </span>
              </label>
            </div>
            <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
              <input
                type="checkbox"
                id="pipeline-hide-value"
                checked={p.hide_value_fields === true}
                onChange={(e) => setP({ ...p, hide_value_fields: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-[#193966]"
                data-testid="admin-hide-value-fields"
              />
              <label htmlFor="pipeline-hide-value" className="text-xs text-slate-700 cursor-pointer">
                <span className="font-medium">Ocultar los campos "Valor" y "Fecha de cierre esperada"</span>
                <span className="block text-[11px] text-slate-500">
                  Útil en pipelines que no manejan montos (ej. Visitas). Los pipelines nuevos los ocultan por defecto.
                </span>
              </label>
            </div>
            <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
              <input
                type="checkbox"
                id="pipeline-cotejar-clientes"
                checked={p.titulo_cotejar_clientes !== false}
                onChange={(e) => setP({ ...p, titulo_cotejar_clientes: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-[#193966]"
                data-testid="admin-cotejar-clientes"
              />
              <label htmlFor="pipeline-cotejar-clientes" className="text-xs text-slate-700 cursor-pointer">
                <span className="font-medium">Cotejar el título del negocio con los clientes existentes</span>
                <span className="block text-[11px] text-slate-500">
                  Activado: el título se elige buscando en la lista de clientes. Desactivado: el título se escribe libremente (útil en cotizaciones con prospectos o clientes nuevos). En ambos casos el nombre se clona al campo Cliente.
                </span>
              </label>
            </div>
            <Button onClick={handleSaveGeneral} disabled={saving} className="bg-[#80BC00] hover:bg-[#6ea700] text-white">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} <Save className="w-4 h-4 mr-1.5" /> Guardar cambios
            </Button>
          </TabsContent>

          {/* ETAPAS */}
          <TabsContent value="etapas" className="pt-3">
            <Button size="sm" onClick={addStage} className="mb-3 bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="admin-add-stage-btn">
              <Plus className="w-3 h-3 mr-1" /> Agregar etapa
            </Button>
            <div className="space-y-2">
              {(p.stages || []).map((s, si) => (
                <div key={s.id} className="border rounded p-3 space-y-2" data-testid={`admin-stage-${s.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex flex-col">
                      <button type="button" onClick={() => moveStage(s, -1)} disabled={si === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-25" data-testid={`stage-up-${s.id}`} title="Subir etapa">
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => moveStage(s, 1)} disabled={si === (p.stages || []).length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-25" data-testid={`stage-down-${s.id}`} title="Bajar etapa">
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                    <Input className="flex-1 min-w-[200px]" value={s.name} onChange={(e) => setP({ ...p, stages: p.stages.map((x) => x.id === s.id ? { ...x, name: e.target.value } : x) })} onBlur={(e) => updateStage(s, { name: e.target.value })} />
                    <Input type="color" value={s.color || "#64748B"} onChange={(e) => updateStage(s, { color: e.target.value })} className="w-12 h-9 p-1" />
                    <div className="flex items-center gap-1">
                      <Label className="text-[10px]">Prob%</Label>
                      <Input type="number" min={0} max={100} className="w-16 h-8" value={s.default_probability ?? 20} onChange={(e) => setP({ ...p, stages: p.stages.map((x) => x.id === s.id ? { ...x, default_probability: Number(e.target.value) } : x) })} onBlur={(e) => updateStage(s, { default_probability: Number(e.target.value) })} />
                    </div>
                    <label className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={!!s.is_won} onChange={(e) => updateStage(s, { is_won: e.target.checked, is_lost: false })} /> Ganada
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={!!s.is_lost} onChange={(e) => updateStage(s, { is_lost: e.target.checked, is_won: false })} /> Perdida
                    </label>
                    <Button size="sm" variant="outline" onClick={() => deleteStage(s)} className="text-rose-600 border-rose-200 h-8">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <details className="rounded border border-slate-200 bg-slate-50" data-testid={`stage-required-details-${s.id}`}>
                    <summary className="text-xs px-2 py-1.5 cursor-pointer text-slate-600 select-none">
                      Campos obligatorios al entrar a esta etapa
                      {(s.required_fields || []).length > 0 && (
                        <Badge variant="outline" className="ml-2 text-[10px] border-[#80BC00]/60 text-[#5f8f00]">{(s.required_fields || []).length}</Badge>
                      )}
                    </summary>
                    <div className="px-2 pb-2 pt-1 space-y-1">
                      <p className="text-[10px] text-slate-500">Al mover un negocio a esta etapa, el sistema pedirá completar estos campos si están vacíos.</p>
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={(s.required_fields || []).includes("expected_close_date")}
                          onChange={(e) => {
                            const cur = s.required_fields || [];
                            updateStage(s, { required_fields: e.target.checked ? [...cur, "expected_close_date"] : cur.filter((n) => n !== "expected_close_date") });
                          }}
                          data-testid={`stage-req-${s.id}-expected_close_date`}
                        /> Fecha de cierre esperada
                      </label>
                      {(p.fields || []).map((f) => (
                        <label key={f.id} className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={(s.required_fields || []).includes(f.name)}
                            onChange={(e) => {
                              const cur = s.required_fields || [];
                              updateStage(s, { required_fields: e.target.checked ? [...cur, f.name] : cur.filter((n) => n !== f.name) });
                            }}
                            data-testid={`stage-req-${s.id}-${f.name}`}
                          /> {f.label}
                        </label>
                      ))}
                      {(p.fields || []).length === 0 && <p className="text-[10px] text-slate-400">Este pipeline no tiene campos personalizados todavía. Podés crear uno acá abajo.</p>}
                      <div className="border-t border-slate-200 pt-2 mt-1">
                        <p className="text-[10px] font-semibold text-slate-600 mb-1">¿No existe el campo? Crealo desde acá:</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Input
                            placeholder="Etiqueta del campo (ej. Fecha de visita)"
                            className="h-8 text-xs flex-1 min-w-[180px]"
                            value={newReqDraft[s.id]?.label || ""}
                            onChange={(e) => setNewReqDraft({ ...newReqDraft, [s.id]: { ...(newReqDraft[s.id] || {}), label: e.target.value } })}
                            data-testid={`stage-newfield-label-${s.id}`}
                          />
                          <select
                            value={newReqDraft[s.id]?.type || "text"}
                            onChange={(e) => setNewReqDraft({ ...newReqDraft, [s.id]: { ...(newReqDraft[s.id] || {}), type: e.target.value } })}
                            className="h-8 border border-slate-300 rounded px-2 text-xs bg-white"
                            data-testid={`stage-newfield-type-${s.id}`}
                          >
                            {FIELD_TYPES.map((ft) => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                          </select>
                          <Button size="sm" onClick={() => createFieldAndRequire(s)} className="h-8 bg-[#80BC00] hover:bg-[#6ea700] text-white text-xs" data-testid={`stage-newfield-create-${s.id}`}>
                            <Plus className="w-3 h-3 mr-1" /> Crear y marcar obligatorio
                          </Button>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1">El campo queda disponible para todo el pipeline (pestaña Campos) y obligatorio al entrar a esta etapa. Si es desplegable, configurá sus opciones en la pestaña Campos.</p>
                      </div>
                    </div>
                  </details>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* CAMPOS */}
          <TabsContent value="campos" className="pt-3">
            <Button size="sm" onClick={addField} className="mb-3 bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="admin-add-field-btn">
              <Plus className="w-3 h-3 mr-1" /> Agregar campo
            </Button>
            <div className="space-y-2">
              {(p.fields || []).map((f, fi) => (
                <div key={f.id} className="border rounded p-3 space-y-2" data-testid={`admin-field-${f.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex flex-col">
                      <button type="button" onClick={() => moveField(f, -1)} disabled={fi === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-25" data-testid={`field-up-${f.id}`} title="Subir campo">
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => moveField(f, 1)} disabled={fi === (p.fields || []).length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-25" data-testid={`field-down-${f.id}`} title="Bajar campo">
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                    <Input className="flex-1 min-w-[180px]" value={f.label} onChange={(e) => setP({ ...p, fields: p.fields.map((x) => x.id === f.id ? { ...x, label: e.target.value } : x) })} onBlur={(e) => updateField(f, { label: e.target.value })} placeholder="Etiqueta" />
                    <select value={f.field_type} onChange={(e) => updateField(f, { field_type: e.target.value })} className="h-9 border border-slate-300 rounded px-2 text-sm bg-white">
                      {FIELD_TYPES.map((ft) => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                    </select>
                    <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={!!f.required} onChange={(e) => updateField(f, { required: e.target.checked })} /> Requerido</label>
                    <Button size="sm" variant="outline" onClick={() => deleteField(f)} className="text-rose-600 border-rose-200 h-8"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                  {(f.field_type === "select" || f.field_type === "multiselect") && (
                    <Input
                      placeholder="Opciones separadas por coma. Ej: Vehículo, Salud, Vida"
                      value={optionsDraft[f.id] !== undefined ? optionsDraft[f.id] : (f.options || []).join(", ")}
                      onChange={(e) => setOptionsDraft({ ...optionsDraft, [f.id]: e.target.value })}
                      onBlur={(e) => {
                        updateField(f, { options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean) });
                        setOptionsDraft((prev) => { const n = { ...prev }; delete n[f.id]; return n; });
                      }}
                      className="text-xs"
                      data-testid={`field-options-${f.id}`}
                    />
                  )}
                  <div className="flex items-center gap-2 flex-wrap rounded border border-slate-200 bg-slate-50 px-2 py-1.5" data-testid={`field-dep-row-${f.id}`}>
                    <Label className="text-[10px] text-slate-600">Visible solo si</Label>
                    <select
                      value={f.depends_on_field || ""}
                      onChange={(e) => updateField(f, { depends_on_field: e.target.value, depends_on_values: [] })}
                      className="h-8 border border-slate-300 rounded px-2 text-xs bg-white"
                      data-testid={`field-dep-select-${f.id}`}
                    >
                      <option value="">— Siempre visible —</option>
                      {(p.fields || []).filter((x) => x.id !== f.id).map((x) => (
                        <option key={x.id} value={x.name}>{x.label}</option>
                      ))}
                    </select>
                    {f.depends_on_field && (() => {
                      const parent = (p.fields || []).find((x) => x.name === f.depends_on_field);
                      if (parent && (parent.options || []).length > 0) {
                        return (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] text-slate-500">tiene el valor:</span>
                            {parent.options.map((o) => (
                              <label key={o} className="flex items-center gap-1 text-[11px]">
                                <input
                                  type="checkbox"
                                  checked={(f.depends_on_values || []).includes(o)}
                                  onChange={(e) => {
                                    const cur = f.depends_on_values || [];
                                    updateField(f, { depends_on_values: e.target.checked ? [...cur, o] : cur.filter((v) => v !== o) });
                                  }}
                                  data-testid={`field-dep-value-${f.id}-${o}`}
                                /> {o}
                              </label>
                            ))}
                          </div>
                        );
                      }
                      return (
                        <Input
                          placeholder="Valores separados por coma"
                          className="text-xs h-8 flex-1 min-w-[160px]"
                          value={depValuesDraft[f.id] !== undefined ? depValuesDraft[f.id] : (f.depends_on_values || []).join(", ")}
                          onChange={(e) => setDepValuesDraft({ ...depValuesDraft, [f.id]: e.target.value })}
                          onBlur={(e) => {
                            updateField(f, { depends_on_values: e.target.value.split(",").map((v) => v.trim()).filter(Boolean) });
                            setDepValuesDraft((prev) => { const n = { ...prev }; delete n[f.id]; return n; });
                          }}
                          data-testid={`field-dep-values-${f.id}`}
                        />
                      );
                    })()}
                    {f.depends_on_field && (f.depends_on_values || []).length === 0 && (
                      <span className="text-[10px] text-amber-600">Sin valores elegidos: el campo se muestra siempre.</span>
                    )}
                  </div>
                  {isAdmin && (
                  <div className="flex items-center gap-2 flex-wrap rounded border border-slate-200 bg-slate-50 px-2 py-1.5" data-testid={`field-stagevis-row-${f.id}`}>
                    <Label className="text-[10px] text-slate-600">Visible solo en etapa(s)</Label>
                    {(p.stages || []).map((s) => (
                      <label key={s.id} className="flex items-center gap-1 text-[11px] text-slate-600">
                        <input
                          type="checkbox"
                          checked={(f.visible_in_stages || []).includes(s.id)}
                          onChange={(e) => {
                            const cur = f.visible_in_stages || [];
                            updateField(f, { visible_in_stages: e.target.checked ? [...cur, s.id] : cur.filter((x) => x !== s.id) });
                          }}
                          className="w-3.5 h-3.5 accent-[#193966]"
                          data-testid={`field-stagevis-${f.id}-${s.id}`}
                        /> {s.name}
                      </label>
                    ))}
                    {(f.visible_in_stages || []).length === 0 && (
                      <span className="text-[10px] text-slate-400">Sin etapas marcadas: visible en todas.</span>
                    )}
                  </div>
                  )}
                  <p className="text-[10px] text-slate-400">Nombre interno: <code>{f.name}</code></p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* PERMISOS */}
          <TabsContent value="permisos" className="pt-3">
            <PermisosTab pipelineId={p.id} />
          </TabsContent>
          <TabsContent value="tareas" className="pt-3">
            <TaskTypesTab pipelineId={p.id} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// PermisosTab — matriz user × pipeline (solo para el pipeline activo)
// ============================================================
function PermisosTab({ pipelineId }) {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState({ users: [], pipelines: [], perms_by_key: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchMatriz = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/pipelines/permisos/matriz`, getAuthHeaders());
      const d = await r.json();
      setData(d);
    } catch { /* silencioso */ }
    setLoading(false);
  };

  useEffect(() => { fetchMatriz(); }, []);  // eslint-disable-line

  const permsByUser = {};
  for (const p of (data.perms_by_key || [])) {
    if (p.pipeline_id !== pipelineId) continue;
    permsByUser[p.user_id] = p;
  }

  const updatePerm = async (userId, patch) => {
    const current = permsByUser[userId] || { user_id: userId, pipeline_id: pipelineId,
      can_view: false, can_create: false, can_edit: false, can_delete: false, can_export: false, can_admin: false };
    const updated = { ...current, ...patch };
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/pipelines/permisos`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(updated),
      });
      if (!r.ok) throw new Error();
      await fetchMatriz();
    } catch { toast.error("No se pudo guardar el permiso"); }
    setSaving(false);
  };

  const filteredUsers = (data.users || []).filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  if (loading) return <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div className="space-y-3" data-testid="permisos-tab">
      <p className="text-xs text-slate-600">
        Definí quién puede <b>ver</b>, <b>crear tarjetas</b>, <b>editar</b>, <b>borrar</b>, <b>exportar</b> o <b>administrar</b> este pipeline.
        Los <b>administradores globales</b> tienen acceso total sin necesidad de configurar acá.
      </p>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Buscar usuario por nombre o email…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-9" data-testid="permisos-search" />
      </div>
      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left px-2 py-1.5 text-xs font-semibold">Usuario</th>
              <th className="px-1 py-1.5 text-xs font-semibold text-center">Ver</th>
              <th className="px-1 py-1.5 text-xs font-semibold text-center">Crear</th>
              <th className="px-1 py-1.5 text-xs font-semibold text-center">Editar</th>
              <th className="px-1 py-1.5 text-xs font-semibold text-center">Borrar</th>
              <th className="px-1 py-1.5 text-xs font-semibold text-center">Export</th>
              <th className="px-1 py-1.5 text-xs font-semibold text-center">Admin</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(u => {
              const isGlobalAdmin = ["admin", "administrador", "superadmin"].includes((u.role || "").toLowerCase());
              const perms = permsByUser[u.id] || {};
              return (
                <tr key={u.id} className="border-b hover:bg-slate-50">
                  <td className="px-2 py-1.5">
                    <div className="flex flex-col">
                      <span className="text-xs font-medium">{u.name}</span>
                      <span className="text-[10px] text-slate-500">{u.email}</span>
                    </div>
                  </td>
                  {["view", "create", "edit", "delete", "export", "admin"].map(action => {
                    const checked = isGlobalAdmin || !!perms[`can_${action}`];
                    return (
                      <td key={action} className="px-1 py-1.5 text-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isGlobalAdmin || saving}
                          onChange={(e) => updatePerm(u.id, { [`can_${action}`]: e.target.checked })}
                          className="w-4 h-4 accent-[#193966]"
                          data-testid={`perm-${u.id}-${action}`}
                          title={isGlobalAdmin ? "Admin global: acceso total" : ""}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {filteredUsers.length === 0 && (
              <tr><td colSpan={7} className="text-center py-4 text-xs text-slate-400">Sin resultados</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-slate-500">
        Tip: marcar <b>Crear/Editar/Borrar/Export</b> automáticamente activa <b>Ver</b>. Marcar <b>Admin</b> activa todo.
      </p>
    </div>
  );
}

// ── Tipos de tarea con SLA (por pipeline) ──
function TaskTypesTab({ pipelineId }) {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [nombre, setNombre] = useState("");
  const [sla, setSla] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}/task-types`, getAuthHeaders());
    const d = await r.json();
    setItems(d.items || []);
  }, [pipelineId, getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!nombre.trim()) return toast.error("Escribe el nombre del tipo de tarea");
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}/task-types`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ name: nombre.trim(), sla_hours: sla ? parseFloat(sla) : null }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Error");
      setNombre(""); setSla("");
      toast.success("Tipo de tarea creado");
      load();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const update = async (t, patch) => {
    await fetch(`${API_URL}/api/pipelines/task-types/${t.id}`, {
      method: "PUT",
      headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
      body: JSON.stringify({ name: patch.name ?? t.name, sla_hours: patch.sla_hours !== undefined ? patch.sla_hours : t.sla_hours }),
    });
    load();
  };

  const remove = async (t) => {
    if (!window.confirm(`¿Eliminar el tipo "${t.name}"? Las tareas existentes conservan su SLA.`)) return;
    await fetch(`${API_URL}/api/pipelines/task-types/${t.id}`, { method: "DELETE", headers: getAuthHeaders().headers });
    toast.success("Tipo eliminado");
    load();
  };

  return (
    <div className="space-y-3" data-testid="admin-task-types">
      <p className="text-xs text-slate-500">
        Define los tipos de tarea de este pipeline y su SLA en horas. Al crear una tarea con un tipo,
        la fecha límite se calcula automáticamente y verás si está <b>en tiempo</b>, <b>por vencer</b> o <b>vencida</b>.
      </p>
      <div className="flex gap-2 items-end flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <label className="text-[10px] font-semibold text-slate-500 uppercase">Nombre del tipo</label>
          <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej: Minuta de reunión" data-testid="tasktype-name-input" />
        </div>
        <div className="w-32">
          <label className="text-[10px] font-semibold text-slate-500 uppercase">SLA (horas)</label>
          <Input type="number" min="0" step="0.5" value={sla} onChange={(e) => setSla(e.target.value)} placeholder="48" data-testid="tasktype-sla-input" />
        </div>
        <Button onClick={add} disabled={saving} className="bg-[#193966] hover:bg-[#122a4d] text-white" data-testid="tasktype-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Agregar
        </Button>
      </div>
      <div className="border rounded-lg divide-y">
        {items.length === 0 && <p className="text-xs text-slate-400 p-3">Aún no hay tipos de tarea. Agrega el primero arriba.</p>}
        {items.map((t) => (
          <div key={t.id} className="flex items-center gap-2 px-3 py-2" data-testid={`tasktype-row-${t.id}`}>
            <Input defaultValue={t.name} onBlur={(e) => e.target.value.trim() && e.target.value !== t.name && update(t, { name: e.target.value.trim() })} className="h-8 text-sm flex-1" />
            <div className="flex items-center gap-1">
              <Input type="number" min="0" step="0.5" defaultValue={t.sla_hours ?? ""} placeholder="—"
                onBlur={(e) => { const v = e.target.value === "" ? null : parseFloat(e.target.value); if (v !== t.sla_hours) update(t, { sla_hours: v }); }}
                className="h-8 w-24 text-sm text-right" data-testid={`tasktype-sla-${t.id}`} />
              <span className="text-[10px] text-slate-400">hrs SLA</span>
            </div>
            <button onClick={() => remove(t)} className="p-1.5 hover:bg-rose-50 rounded" data-testid={`tasktype-del-${t.id}`}>
              <Trash2 className="w-4 h-4 text-rose-500" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
